<template>
  <div class="public-app" :style="pageStyle">
    <div class="container py-4">
      <div v-if="loading" class="text-center py-5">
        <div class="public-loading">
          <div class="skeleton" style="width: 300px; height: 24px; margin: 0 auto 12px"></div>
          <div class="skeleton" style="width: 200px; height: 16px; margin: 0 auto"></div>
        </div>
      </div>

      <div v-else-if="error" class="text-center py-5 fade-in">
        <div class="error-icon">
          <i class="bi bi-exclamation-circle"></i>
        </div>
        <h3 style="color: var(--color-gray-700, #374151)">{{ errorMessage }}</h3>
        <p style="color: var(--color-gray-500, #6b7280)">Sprawdź poprawność linku.</p>
      </div>

      <template v-else-if="survey">
        <template v-if="requiresPassword && !passwordVerified && !completed">
          <div class="row justify-content-center fade-in">
            <div class="col-md-6 col-lg-5">
              <div class="card" style="border-radius: 16px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1)">
                <div class="card-body p-4">
                  <div class="text-center mb-3">
                    <i class="bi bi-lock" style="font-size: 2rem; color: var(--color-primary, #4f46e5)"></i>
                  </div>
                  <h5 class="text-center fw-bold mb-1">{{ survey.title }}</h5>
                  <p class="text-center text-muted small mb-4">Ten formularz wymaga hasła.</p>
                  <form @submit.prevent="verifyPassword">
                    <div class="mb-3">
                      <input
                        v-model="password"
                        type="password"
                        class="form-control"
                        placeholder="Wpisz hasło"
                        required
                      />
                    </div>
                    <div v-if="passwordError" class="text-danger small mb-2">
                      {{ passwordError }}
                    </div>
                    <button type="submit" class="btn btn-primary w-100">Kontynuuj</button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="fade-in">
            <SurveyFill
              :survey="survey"
              :upload-url="`/api/public/surveys/${slug}/files`"
              :download-url="`/api/public/surveys/${slug}/files`"
              @completed="handleCompleted"
            />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import SurveyFill from './views/public/SurveyFill.vue';

const loading = ref(true);
const error = ref(false);
const errorMessage = ref('Formularz nie istnieje');
const survey = ref(null);
const completed = ref(false);
const slug = ref('');
const requiresPassword = ref(false);
const passwordVerified = ref(false);
const password = ref('');
const passwordError = ref('');

const pageStyle = computed(() => {
  const vars = survey.value?.theme?.cssVariables;
  if (!vars) return {};
  const style = {};
  if (vars['--sjs-general-backcolor-dim']) {
    style.backgroundColor = vars['--sjs-general-backcolor-dim'];
  }
  if (vars['--sjs-general-forecolor']) {
    style.color = vars['--sjs-general-forecolor'];
  }
  return style;
});

onMounted(async () => {
  const path = window.location.pathname;
  const match = path.match(/^\/s\/(.+)$/);
  if (!match) {
    error.value = true;
    loading.value = false;
    return;
  }

  slug.value = match[1];

  try {
    const { data } = await axios.get(`/api/public/surveys/${slug.value}`, {
      headers: { Accept: 'application/json' },
    });
    survey.value = data.data;
    requiresPassword.value = !!data.data.requires_password;
  } catch (err) {
    error.value = true;
    if (err.response?.data?.message) {
      errorMessage.value = err.response.data.message;
    }
  } finally {
    loading.value = false;
  }
});

function verifyPassword() {
  passwordError.value = '';
  passwordVerified.value = true;
}

async function handleCompleted(responseData) {
  try {
    const payload = { data: responseData };
    if (requiresPassword.value) {
      payload.password = password.value;
    }
    await axios.post(`/api/public/surveys/${slug.value}/submit`, payload, {
      headers: { Accept: 'application/json' },
    });
    completed.value = true;
  } catch (err) {
    if (err.response?.status === 403 && requiresPassword.value) {
      passwordError.value = err.response?.data?.message || 'Nieprawidłowe hasło.';
      passwordVerified.value = false;
    } else if (err.response?.status === 403) {
      error.value = true;
      errorMessage.value = err.response?.data?.message || 'Formularz nie jest dostępny.';
    }
  }
}
</script>

<style scoped>
.public-app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.public-app .container {
  flex: 1;
}

.error-icon {
  font-size: 3rem;
  color: var(--color-gray-300, #d1d5db);
  margin-bottom: 1rem;
}

.powered-by {
  text-align: center;
  padding: 1.5rem;
  font-size: 0.75rem;
  opacity: 0.4;
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
